import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { Paged } from '@core/api/masters';
import type {
  BillableReceiptLine,
  PurchaseBill,
  PurchaseBillFilters,
  PurchaseBillPreview,
  PurchaseBillSummary,
  SavePurchaseBillInput,
} from '@core/api/purchaseBills';
import { billablePath, purchaseBillPath, purchaseBillsPath } from '@core/api/queryPaths';

export type {
  BillableReceiptLine,
  PurchaseBillPreview,
  PurchaseBill,
  PurchaseBillLine,
  PurchaseBillSummary,
} from '@core/api/purchaseBills';

export const purchaseBillsKey = ['purchase-bill'] as const;

export function usePurchaseBills(branchId: number | undefined, filters: PurchaseBillFilters = {}) {
  return useQuery({
    queryKey: [...purchaseBillsKey, { branchId, ...filters }],
    queryFn: () => apiFetch<Paged<PurchaseBillSummary>>(purchaseBillsPath(filters)),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function usePurchaseBill(id: number | null) {
  return useQuery({
    queryKey: [...purchaseBillsKey, 'one', id],
    queryFn: () => apiFetch<PurchaseBill>(purchaseBillPath(id ?? 0)),
    enabled: id !== null,
  });
}

/** What the chosen supplier has delivered and not yet billed. */
export function useBillable(supplierVendorId: number | null) {
  return useQuery({
    queryKey: [...purchaseBillsKey, 'billable', supplierVendorId],
    queryFn: () => apiFetch<BillableReceiptLine[]>(billablePath(supplierVendorId ?? 0)),
    enabled: supplierVendorId !== null,
  });
}

/** The bill worked out by the server as saving would — only once it could be saved. */
export function usePurchaseBillPreview(input: SavePurchaseBillInput | null) {
  return useQuery({
    queryKey: [...purchaseBillsKey, 'preview', input],
    queryFn: () =>
      apiFetch<PurchaseBillPreview>('/api/purchase-bill/preview', { method: 'POST', body: input }),
    enabled: input !== null,
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export const cancelPurchaseBill = (id: number, reason: string) =>
  apiFetch<PurchaseBill>(`/api/purchase-bill/${id}/cancel`, { method: 'POST', body: { reason } });

export const acceptBillDifferences = (id: number, reason: string) =>
  apiFetch<PurchaseBill>(`/api/purchase-bill/${id}/accept`, { method: 'POST', body: { reason } });

export { createPurchaseBill } from '@core/api/purchaseCommands';
